import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Send, 
  MessageSquare, 
  CloudRain, 
  Zap, 
  Users, 
  Crown,
  Trophy,
  MoreVertical,
  Smile,
  Info
} from "lucide-react";
import { useSelector } from "react-redux";
import { UserModelData } from "../../../types";
import useThemeHook from "../../hooks/useThemeHook";
import { THEME_COLORS } from "../../themes/DarkLight";
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  onSnapshot, 
  addDoc, 
  serverTimestamp,
  Timestamp 
} from "firebase/firestore";
import { db } from "../../../backend/ArthereumProvider";
import CommonNavBar from "../../components/CommonNavBar";

interface Message {
  id: string;
  userId: string;
  username: string;
  text: string;
  isRain?: boolean;
  isWinner?: boolean;
  createdAt: any;
}

const CommunityChatPage: React.FC = () => {
  const theme = useThemeHook();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Real-time messages listener
  useEffect(() => {
    const q = query(
      collection(db, "community_messages"),
      orderBy("createdAt", "desc"),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const msgs: Message[] = [];
      querySnapshot.forEach((doc) => {
        msgs.push({ id: doc.id, ...doc.data() } as Message);
      });
      setMessages(msgs.reverse());
    });

    return () => unsubscribe();
  }, []);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || !user) return;

    setSending(true);
    try {
      await addDoc(collection(db, "community_messages"), {
        userId: user.id,
        username: user.username,
        text: newMessage,
        createdAt: serverTimestamp()
      });
      setNewMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`flex flex-col h-screen ${theme === "dark" ? "bg-slate-900" : "bg-slate-50"}`}>
      <CommonNavBar title="Community Chat" />
      
      {/* Active Users Ticker */}
      <div className={`flex items-center gap-2 px-4 py-2 border-b ${theme === "dark" ? "bg-slate-800/50 border-slate-700" : "bg-white border-slate-100"}`}>
        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        <span className={`text-[10px] font-bold ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
          1,284 MINERS ONLINE
        </span>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => {
          const isMe = msg.userId === user?.id;
          const isSystem = msg.userId === "system";
          const isRain = msg.isRain;

          if (isRain) {
            return (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex justify-center my-4"
              >
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 rounded-2xl shadow-lg shadow-blue-500/20 text-center">
                   <div className="flex items-center justify-center gap-2 mb-1">
                     <CloudRain className="text-white animate-bounce" size={18} />
                     <span className="text-white font-black text-xs uppercase tracking-widest">Rain Alert</span>
                   </div>
                   <p className="text-white text-xs font-bold leading-tight">
                     {msg.text}
                   </p>
                </div>
              </motion.div>
            );
          }

          return (
            <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] ${isMe ? "items-end" : "items-start"} flex flex-col`}>
                {!isMe && (
                  <span className={`text-[10px] font-black mb-1 ml-1 uppercase tracking-tighter ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`}>
                    {msg.username}
                  </span>
                )}
                <div className={`
                  px-4 py-2.5 rounded-2xl text-sm leading-relaxed
                  ${isMe 
                    ? "bg-indigo-600 text-white rounded-tr-none shadow-lg shadow-indigo-600/10" 
                    : theme === "dark"
                      ? "bg-slate-800 text-slate-200 border border-slate-700 rounded-tl-none"
                      : "bg-white text-slate-900 border border-slate-100 rounded-tl-none shadow-sm"
                  }
                `}>
                  {msg.text}
                </div>
                <span className={`text-[8px] mt-1 opacity-50 ${isMe ? "mr-1" : "ml-1"}`}>
                  12:45 PM
                </span>
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      {/* Input Area */}
      <div className={`p-4 pb-10 ${theme === "dark" ? "bg-slate-800/80 border-t border-slate-700" : "bg-white border-t border-slate-100"}`}>
        <form onSubmit={handleSendMessage} className="relative flex items-center gap-2">
          <button type="button" className={`p-2 transition-colors ${theme === "dark" ? "text-slate-500 hover:text-slate-300" : "text-slate-400 hover:text-slate-600"}`}>
            <Smile size={20} />
          </button>
          
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className={`
              flex-1 py-3 px-4 rounded-2xl text-sm transition-all outline-none
              ${theme === "dark" 
                ? "bg-slate-900 border-slate-700 text-white placeholder-slate-600 focus:border-indigo-600" 
                : "bg-slate-50 border-slate-100 text-slate-900 placeholder-slate-400 focus:border-indigo-500"
              }
              border
            `}
          />
          
          <motion.button
            whileTap={{ scale: 0.95 }}
            type="submit"
            disabled={!newMessage.trim() || sending}
            className={`
              w-11 h-11 rounded-xl flex items-center justify-center transition-all
              ${newMessage.trim() 
                ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/20" 
                : "bg-slate-200 text-slate-400 dark:bg-slate-700 dark:text-slate-500"
              }
            `}
          >
            <Send size={18} />
          </motion.button>
        </form>
      </div>
    </div>
  );
};

export default CommunityChatPage;
